import { SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';
import type { CommendaServicesResponse } from '../../commenda-services/types';
import {
  SERVICE_DELIVERY_DEDUPING_INTERVAL_MS,
  SERVICE_DELIVERY_DEFAULT_PAGE_SIZE,
  SERVICE_DELIVERY_MIN_SEARCH_LENGTH,
} from './constants';
import {
  buildServiceDeliveryQuery,
  buildServiceDeliverySummaryQuery,
} from './query';
import type {
  ServiceDeliveryCatalogOptionsQuery,
  ServiceDeliveryFilters,
  ServiceDeliveryGroup,
  ServiceDeliveryIdentity,
  ServiceDeliveryMemberOption,
  ServiceDeliveryOptionsQuery,
  ServiceDeliveryPage,
  ServiceDeliveryPortfolioQuery,
  ServiceDeliveryRow,
  ServiceDeliveryServicesQuery,
  ServiceDeliverySummaryResponse,
} from './types';

const readOptions = {
  // Keep cached dashboard results until Refresh; new query keys still load.
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  refreshInterval: 0,
  shouldRetryOnError: false,
  dedupingInterval: SERVICE_DELIVERY_DEDUPING_INTERVAL_MS,
};

export function useServiceDeliveryServices(
  query: ServiceDeliveryServicesQuery | null
) {
  return useSWR<ServiceDeliveryPage<ServiceDeliveryRow>>(
    query ? buildServiceDeliveryQuery(query) : null,
    SWRFetcher({ method: 'GET' }),
    { ...readOptions, keepPreviousData: true }
  );
}
export function useServiceDeliveryPortfolios(
  query: ServiceDeliveryPortfolioQuery | null
) {
  return useSWR<ServiceDeliveryPage<ServiceDeliveryGroup>>(
    query ? buildServiceDeliveryQuery(query) : null,
    SWRFetcher({ method: 'GET' }),
    { ...readOptions, keepPreviousData: true }
  );
}
export function useServiceDeliverySummary(
  filters: ServiceDeliveryFilters | null
) {
  return useSWR<ServiceDeliverySummaryResponse>(
    filters ? buildServiceDeliverySummaryQuery(filters) : null,
    SWRFetcher({ method: 'GET' }),
    readOptions
  );
}
export function fetchServiceDeliveryOptions(
  query: ServiceDeliveryOptionsQuery
): Promise<ServiceDeliveryPage<ServiceDeliveryIdentity>> {
  return SWRFetcher({ method: 'GET' })(buildServiceDeliveryQuery(query));
}
export function fetchServiceDeliveryMemberOptions(
  query: Omit<ServiceDeliveryOptionsQuery, 'view' | 'optionsOnly'>
): Promise<ServiceDeliveryPage<ServiceDeliveryMemberOption>> {
  return SWRFetcher({ method: 'GET' })(
    buildServiceDeliveryQuery({
      ...query,
      view: 'AGENT_FIRM_MEMBERS',
      optionsOnly: true,
    })
  );
}
export function fetchServiceDeliveryCatalogOptions({
  search = '',
  offset = 0,
  limit = SERVICE_DELIVERY_DEFAULT_PAGE_SIZE,
  serviceCategories,
  serviceSubcategories,
  serviceCompositeIds,
}: ServiceDeliveryCatalogOptionsQuery): Promise<CommendaServicesResponse> {
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
  });
  if (search.trim()) params.set('query', search.trim());
  serviceCategories?.forEach((value) => params.append('categories[]', value));
  serviceSubcategories?.forEach((value) =>
    params.append('subcategories[]', value)
  );
  serviceCompositeIds?.forEach((value) =>
    params.append('serviceCompositeIds[]', value)
  );
  return SWRFetcher({ method: 'GET' })(`commenda-services?${params}`);
}
/** Customer options for pickers; searches shorter than the backend minimum list the first page. */
export function useServiceDeliveryCustomerOptions(search: string) {
  const term = search.trim();
  return useSWR<ServiceDeliveryPage<ServiceDeliveryIdentity>>(
    buildServiceDeliveryQuery({
      view: 'CUSTOMERS',
      optionsOnly: true,
      search:
        term.length >= SERVICE_DELIVERY_MIN_SEARCH_LENGTH ? term : undefined,
      limit: SERVICE_DELIVERY_DEFAULT_PAGE_SIZE,
    }),
    SWRFetcher({ method: 'GET' }),
    { ...readOptions, keepPreviousData: true }
  );
}
export function useServiceDeliveryCatalogOptions(search: string) {
  const params = new URLSearchParams({
    offset: '0',
    limit: String(SERVICE_DELIVERY_DEFAULT_PAGE_SIZE),
  });
  if (search.trim()) params.set('query', search.trim());
  return useSWR<CommendaServicesResponse>(
    `commenda-services?${params}`,
    SWRFetcher({ method: 'GET' }),
    { ...readOptions, keepPreviousData: true }
  );
}
